const SWIPE = {
    MIN_DISTANCE: 30,
    MAX_DURATION: 300,
    MAX_OFF_AXIS: 30,
    MIN_VELOCITY: 0.5
};

if (!customElements.get('interactive-zoom')) {
    class InteractiveZoom extends HTMLElement {
        hasTouch = false;

        config = {
            zoom: {
                min: 1,
                max: 4,
                steps: 4,
                threshold: { x: 2, y: 2 }
            }
        }

        input = {
            start: 0,
            taps: {
                last: 0,
                count: 0
            },
            max: 0,
            count: 0,
            initial: { x: 0, y: 0, screenX: 0, screenY: 0, translate: { x: 0, y: 0 } },
            last: { x: 0, y: 0, screenX: 0, screenY: 0 },
            delta: { x: 0, y: 0, screenX: 0, screenY: 0 },
            moved: false
        }

        scale = 1;

        translate = {
            x: 0, y: 0
        }

        constructor() {
            super();       
        }

        connectedCallback() {
            this.stage = this.querySelector('interactive-zoom-stage');
            if (!this.stage) {
                console.error("No stage found.");
                return;
            }

            this.images = [...this.stage.querySelectorAll('[data-interactive-image]')].reduce((acc, image) => {
                const imageType = image.dataset.interactiveImage ?? "main";
                if (acc[imageType]) { acc[imageType].remove() }
                acc[imageType] = image;
                return acc;
            }, {})

            if (!this.images["main"]) {
                console.error("No main image found.");
                return;
            }

            this.gallery = {
                element: this.closest('media-gallery'),
                component: this.closest('[data-media-id]')
            }

            try {
                const config = JSON.parse(this.dataset.interactiveZoom)
                this.config.zoom.max_width = config.max_width;
            } catch(e){
                console.error("Invalid config: " + e)
            }

            this.scale = 1; 

            this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false })
            this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false })
            this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false })
            this.addEventListener('touchcancel', this.onTouchEnd.bind(this), { passive: false })
            this.addEventListener('mousedown', this.onMouseDown.bind(this), { passive: false })
            document.addEventListener('mousemove', this.onMouseMove.bind(this), { passive: false })
            document.addEventListener('mouseup', this.onMouseUp.bind(this), { passive: false })
            window.addEventListener('resize', () => this.calculateMaxZoom());
            this.calculateMaxZoom();

            const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        this.calculateMaxZoom();
                    } else {
                        this.applyTransform({scale: 1, translate: {x: 0, y: 0}});
                    }
                }
            },
            {
                threshold: 0.01 // triggers as soon as even 1% is visible
            }
            );
            observer.observe(this);
        }

        calculateMaxZoom(){
            if(this.config.zoom.max_width){
                this.config.zoom.max = this.config.zoom.max_width / this.clientWidth
            }
        }

        //Touch inputs
        onTouchStart(event) {
            this.hasTouch = true;
            this.input.count = event.touches.length;
            this.input.max = this.input.max + 1;
            this.input.start = performance.now();

            if (this.input.count === 1) {
                this.input.initial = {
                    x: event.changedTouches[0].clientX - this.translate.x,
                    y: event.changedTouches[0].clientY - this.translate.y,
                    screenX: event.changedTouches[0].screenX,
                    screenY: event.changedTouches[0].screenY,
                    translate: this.translate
                };

                this.input.last.screenX = event.changedTouches[0].screenX;
                this.input.last.screenY = event.changedTouches[0].screenY;

                this.input.delta = { x: 0, y: 0, screenX: 0, screenY: 0 };
            } else {
                this.input.taps = { last: 0, count: 0 }

                if (this.input.count === 2) {
                    this.input.initial.distance = Math.hypot(
                        event.touches[1].clientX - event.touches[0].clientX,
                        event.touches[1].clientY - event.touches[0].clientY
                    );
                    this.input.initial.scale = this.scale;
                }
            }
        }

        onTouchMove(event) {
            if (this.input.count === 1) {
                if (this.input.max === 1) {
                    const touch = event.changedTouches[0];
                    this.pan({ x: touch.clientX, y: touch.clientY }, { x: this.input.last.screenX - touch.screenX, y: this.input.last.screenY - touch.screenY });

                    this.input.last.screenX = touch.screenX;
                    this.input.last.screenY = touch.screenY;
                }
            } else if (this.input.count === 2) {
                const t1 = event.touches[0];
                const t2 = event.touches[1];

                const dist = Math.hypot(
                    t2.clientX - t1.clientX,
                    t2.clientY - t1.clientY
                );

                const scaleFactor = dist / this.input.initial.distance;

                const center = {
                    x: (t1.clientX + t2.clientX) / 2,
                    y: (t1.clientY + t2.clientY) / 2
                };

                const scale = this.input.initial.scale * scaleFactor;
                this.zoom(center, scale);
            }
        }

        onTouchEnd(event) {
            this.input.count = event.touches.length;
            if (event.touches.length !== 0) return; // Still an active touch somewhere

            const touch = event.changedTouches[0];

            //Only double tap if there have been 1 active touches
            if (this.input.max === 1) {
                const now = performance.now();

                const distance = {
                    x: Math.abs(this.input.initial.screenX - touch.screenX),
                    y: Math.abs(this.input.initial.screenY - touch.screenY)
                }

                if (distance.x < SWIPE.MIN_DISTANCE && distance.y < SWIPE.MIN_DISTANCE) {
                    //This is a tap
                    if (this.input.taps.count === 0) {
                        this.input.taps.count = 1;
                    } else {
                        if (now - this.input.taps.last < 300) {
                            this.input.taps.count = this.input.taps.count + 1;
                        } else {
                            this.input.taps.count = 1;
                        }
                    }

                    if (this.input.taps.count >= 2) {
                        this.input.taps.count = 0; //Reset tap count                        
                        this.zoomFixed({ x: touch.clientX, y: touch.clientY });
                    }
                    this.input.taps.last = now;
                } else {
                    const duration = now - this.input.start;
                    const velocity = distance.x / duration;

                    const localDistance = {
                        x: Math.abs(this.input.initial.x - (touch.clientX - this.translate.x)),
                        y: Math.abs(this.input.initial.y - (touch.clientY - this.translate.y))
                    }

                    if (localDistance.x >= SWIPE.MIN_DISTANCE && velocity >= SWIPE.MIN_VELOCITY && distance.y <= SWIPE.MAX_OFF_AXIS && duration <= SWIPE.MAX_DURATION && (this.input.initial.translate.x - this.translate.x) === 0) {
                        const dir = (this.input.initial.screenX - touch.screenX) > 0 ? 1 : -1
                        this.swipe(dir)
                    }
                }
            }

            //No more active touches so reset the max
            this.input.max = 0;
        }


        //Mouse inputs
        onMouseDown(event) {
            if (this.hasTouch || event.buttons !== 1) return
            this.input.count = 1;
            this.input.initial = { x: event.clientX - this.translate.x, y: event.clientY - this.translate.y };
            this.input.delta = { x: 0, y: 0 };
            this.input.start = performance.now();
        }

        onMouseMove(event) {
            if (this.hasTouch || this.input.count !== 1) return

            this.input.delta.screenX = this.input.initial.screenX - event.screenX;
            this.input.delta.screenY = this.input.initial.screenY - event.screenY;

            this.pan({ x: event.clientX, y: event.clientY });
        }

        onMouseUp(event) {
            if (this.hasTouch) return
            if (this.input.count === 1) {
                if (Math.abs(this.input.delta.x) <= this.config.zoom.threshold.x && Math.abs(this.input.delta.y) <= this.config.zoom.threshold.y) {
                    this.zoomFixed({ x: event.clientX, y: event.clientY })
                }
            }
            this.onMouseEnd();
        }

        onMouseEnd() {
            if (this.hasTouch) return
            this.input.count = 0;
        }

        pan(target, d) {
            this.input.delta.x = target.x - this.input.initial.x;
            this.input.delta.y = target.y - this.input.initial.y;

            if (this.scale > 1) {
                this.applyTransform({translate: this.clampTranslate(this.input.delta.x, this.input.delta.y)});                
            }

            if (this.hasTouch && (this.input.initial.translate.y - this.translate.y) === 0) {
                window.scrollBy(0, d?.y ?? 0)
            }
        }

        /* ==========
            Rendering
        ========== */

        applyTransform({ scale = this.scale, translate = this.translate }) {
            this.scale = scale;
            this.translate = translate;
            this.scheduleRender();
        }

        scheduleRender() {
            if (this._raf) return;
            this._raf = requestAnimationFrame(() => {
                this._raf = null;
                this.transformImages();
            });
        }

        transformImages() {
            this.style.setProperty('--pan-x', `${this.translate.x}px`);
            this.style.setProperty('--pan-y', `${this.translate.y}px`);            
            this.style.setProperty('--scale', this.scale);
        }


        /* ==========
            Zooming
        ========== */
        zoomFixed(target) {
            const amount = (this.config.zoom.max - 1) / this.config.zoom.steps;
            const scale = (this.scale + amount) > (this.config.zoom.max + (amount / 2)) ? 1 : this.scale + amount;
            this.zoom(target, scale)
        }

        zoom(target, targetScale) {
            this.loadZoomedImage();

            const prevScale = this.scale;
            const scale = this.clamp(Math.round(targetScale * 100) / 100, 1, this.config.zoom.max);

            const containerRect = this.getBoundingClientRect();

            // Click point relative to container
            const containerTarget = {
                x: target.x - containerRect.left,
                y: target.y - containerRect.top
            };

            // Click point relative to image
            const imagePoint = this.screenToImage(containerTarget, prevScale);

            // Convert back to screen space after zoom
            const screenPoint = this.imageToScreen(imagePoint, scale);

            // Adjust translate to keep the zoomed point under the cursor
            const translate = this.clampTranslate(
                containerTarget.x - screenPoint.x,
                containerTarget.y - screenPoint.y,
                scale
            );

            this.applyTransform({ scale, translate });
        }

        swipe(dir) {
            if (!this.gallery.element || !this.gallery.component) return;

            const getNext = (el) => {
                let n = el.nextElementSibling;
                while (n && n.tagName !== el.tagName) {
                    n = n.nextElementSibling;
                }
                return n;
            }

            const getPrev = (el) => {
                let p = el.previousElementSibling;
                while (p && p.tagName !== el.tagName) {
                    p = p.previousElementSibling;
                }
                return p;
            }

            const target = dir === -1 ? getPrev(this.gallery.component) : getNext(this.gallery.component)

            if (!target) return;
            try {
                this.gallery.element.setActiveMedia(target.dataset.mediaId)
            } catch (e) {
                console.error(e)
            }
        }

        removeDefaultImage() {
            if (!this.images["main"] || !this.images["zoomed"]) return;
            setTimeout(() => {
                this.images["main"].remove();
                this.images["main"] = this.images["zoomed"]
                delete this.images["zoomed"]
            }, 1000)
        }

        loadZoomedImage() {
            const zoomedImage = this.images["zoomed"];
            if (!zoomedImage || zoomedImage.revealed) return

            if (!zoomedImage.complete) {
                zoomedImage.style.opacity = 0;
                zoomedImage.addEventListener('load', () => {
                    setTimeout(() => {
                        zoomedImage.style.opacity = 1;
                        this.removeDefaultImage();
                    }, 50)                    
                })
            } else {
                //Already loaded
                this.removeDefaultImage()
            }

            zoomedImage.style.display = "block";
            zoomedImage.revealed = true;
        }

        clampTranslate(x, y, scale = this.scale) {
            const imageWidth = scale === this.scale ? {
                x: this.images["main"].clientWidth,
                y: this.images["main"].clientHeight
            } : {
                x: (this.images["main"].clientWidth / this.scale) * scale,
                y: (this.images["main"].clientHeight / this.scale) * scale
            }

            const overflowX = imageWidth.x - this.clientWidth;
            const overflowY = imageWidth.y - this.clientHeight;

            return {
                x: overflowX > 0 ? this.clamp(x, -overflowX / 2, overflowX / 2) : 0,
                y: overflowY > 0 ? this.clamp(y, -overflowY / 2, overflowY / 2) : 0
            };
        }

        clamp(number, min, max) {
            return Math.max(min, Math.min(number, max));
        }

        screenToImage(point, scale = this.scale, translate = this.translate) {
            return {
                x: (point.x - (this.clientWidth / 2) - translate.x) / scale,
                y: (point.y - (this.clientHeight / 2) - translate.y) / scale
            };
        }

        imageToScreen(point, scale = this.scale) {
            return {
                x: point.x * scale + (this.clientWidth / 2),
                y: point.y * scale + (this.clientHeight / 2)
            };
        }
    }
    customElements.define('interactive-zoom', InteractiveZoom);
}